import React from "react";

import { datadogRum } from "@datadog/browser-rum";
import { initializeFeatureFlagProvider } from "@worktango/feature-flags-react";
import { createRoot } from "react-dom/client";

import { minimalClientI18n } from "@kazoohr/internationalization";

import { locales } from "../locales";

import { ErrorBoundary } from "./components/ErrorBoundary";
import { ConstellationRoutes } from "./ConstellationRoutes";
import { resolveInitialLanguage, setCurrentLanguage } from "./helpers/language";
import { Providers } from "./Providers";
import "@worktango/d2c-service/window";
import "./styles/colors.scss";

/**
 * This looks weird because we want to show a loader for _at least_ 500ms
 * before showing the app, for now. This provides some quick user feedback
 * while we load assets and parse our JS.
 */
setTimeout(() => {
  datadogRum.init({
    applicationId: "ef602997-9e86-418b-a199-db0d129b697b",
    clientToken: "pub843332fa7a400bdb18c83060e9be2aaa",
    site: "us5.datadoghq.com",
    service: "constellation",
    env: window.__APP_DATA__?.env ?? "unknown",
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    defaultPrivacyLevel: "mask-user-input",
  });

  const pendoApiKey = window.__APP_DATA__?.pendoConfig?.apiKey;
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  if (pendoApiKey && (window as any).loadPendo) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    (window as any).loadPendo(pendoApiKey);
  }

  const rootElement = document.getElementById("root");
  if (!rootElement) {
    throw new Error("Failed to find root element");
  }

  if (!window.kazoo) {
    // This is a hack to make sure we have a kazoo object in the window
    const windowAsAny: any = window;
    windowAsAny.kazoo = {};
  }

  const sdkKey = window.__APP_DATA__?.featureFlagConfiguration?.clientKey;
  const enableDebugger =
    window.__APP_DATA__?.featureFlagConfiguration?.enableDebugger;
  if (sdkKey) {
    initializeFeatureFlagProvider({
      sdkKey,
      shouldEnableDebugger: enableDebugger,
    });
    console.log("Feature Flag SDK initialized"); // eslint-disable-line no-console
  } else {
    console.log("No Feature Flag SDK key provided, skipping"); // eslint-disable-line no-console
  }

  void renderApp(rootElement);
}, 10);

async function renderApp(rootElement: HTMLElement) {
  const root = createRoot(rootElement);

  const language = resolveInitialLanguage();
  setCurrentLanguage(language);
  await minimalClientI18n({ locales, language });

  root.render(
    <ErrorBoundary>
      <Providers>
        <ConstellationRoutes />
      </Providers>
    </ErrorBoundary>
  );

  setTimeout(() => {
    const toRemove = document.querySelectorAll(".hide-on-main-js-loaded");
    toRemove.forEach((el) => {
      if (el instanceof HTMLElement) {
        el.style.display = "none";
      }
    });
    rootElement.style.opacity = "";
    rootElement.style.zIndex = "";
  }, 250);
}
